/**
 * Types for a parsed MusicXML score, its summary and its linear note timeline.
 *
 * Positions inside a measure are in quarter notes ("beats" below always means quarter notes,
 * whatever the time signature), so they do not depend on MusicXML `divisions`.
 */

export type VoicePart = 'S' | 'A' | 'T' | 'B';
export const VOICE_PARTS: readonly VoicePart[] = ['S', 'A', 'T', 'B'];

export type PartRole = 'voice' | 'accompaniment';

/** How a part was assigned its role and voice part. */
export type MatchedBy = 'name' | 'order' | 'manual' | null;

/** What the director can assign to a part on the upload screen. */
export type PartAssignment = VoicePart | 'accompaniment';

export interface ClefInfo {
  staff: number;
  sign: string;
  line: number | null;
  octaveChange: number;
  kind: ClefKind;
}

export type ClefKind =
  | 'treble'
  | 'treble8vb'
  | 'treble8va'
  | 'bass'
  | 'bass8vb'
  | 'alto'
  | 'tenor'
  | 'percussion'
  | 'other';

export interface Lyric {
  verse: number;
  text: string;
  syllabic: 'single' | 'begin' | 'middle' | 'end';
}

/** How a pitch is written: the note name and accidental as in the score (e.g. D flat, not C sharp). */
export interface Spelling {
  step: string;
  alter: number;
  octave: number;
}

/**
 * Which line of a divided part (divisi) sings a note. A part divides where its staff holds two
 * voices or chords, or where two staves are assigned the same voice part (Soprano 1 and 2).
 * 0: every line (the part is not divided there, or sings in unison); 1: the upper line only;
 * 2: the lower line only; 3: an inner note of a three-way division, which no line sings.
 */
export type NoteLine = 0 | 1 | 2 | 3;

/** The line a chorist sings in a divided part: 1 is the upper ("1. Soprano"), 2 the lower. */
export type PartLine = 1 | 2;

/** A pitched note as written in one measure of one part (before repeats are unrolled). */
export interface RawNote {
  measureIndex: number;
  /** Start inside the measure, in quarter notes. */
  offset: number;
  /** Duration in quarter notes. */
  duration: number;
  /** Sounding MIDI pitch (can be fractional for microtonal alterations). */
  midi: number;
  /** The written spelling, for showing note names. */
  spelling: Spelling;
  staff: number;
  voice: string;
  tieStart: boolean;
  tieStop: boolean;
  lyrics: Lyric[];
  /** The line of a divided part that sings it (set once the part is read). */
  line: NoteLine;
}

/** A rest as written: a visible rest in another voice means that line is silent there. */
export interface RawRest {
  measureIndex: number;
  offset: number;
  duration: number;
  staff: number;
  voice: string;
  /** False for rests hidden in the score (print-object="no"). */
  visible: boolean;
}

export interface TempoMark {
  measureIndex: number;
  /** Offset inside the measure, in quarter notes. */
  offset: number;
  /** Tempo in quarter notes per minute. */
  quarterBpm: number;
  /** As shown in the metronome mark, when there is one. */
  beatUnit: string | null;
  dotted: boolean;
  perMinute: number | null;
}

export interface EndingMark {
  numbers: number[];
  type: 'start' | 'stop' | 'discontinue';
}

/** Structural information for one measure, merged across all parts. */
export interface MeasureInfo {
  index: number;
  /** The measure's `number` attribute, e.g. "1", "0" for a pickup, or "12a". */
  number: string;
  implicit: boolean;
  /** Time signature in force for this measure. */
  timeSignature: { beats: number; beatType: number };
  /** True if a `<time>` element appears in this measure. */
  timeSignatureChange: boolean;
  /** Length in quarter notes: the longest content of any part, or the time signature. */
  length: number;
  forwardRepeat: boolean;
  /** `afterJump`: the repeat is played again after a D.S. or D.C. (MusicXML 4.0 `after-jump="yes"` on the repeat sign). */
  forwardRepeatAfterJump?: boolean;
  backwardRepeat: { times: number; afterJump?: boolean } | null;
  endingStart: EndingMark | null;
  endingStop: EndingMark | null;
  /** Ending (volta) this measure belongs to, filled in after all measures are read. */
  ending: { numbers: number[]; groupMax: number } | null;
  segno: boolean;
  coda: boolean;
  toCoda: boolean;
  daCapo: boolean;
  dalSegno: boolean;
  /** The jump in this measure is written "con rep." (or "with repeats"): after it, repeats are played as before. */
  jumpWithRepeats?: boolean;
  fine: boolean;
  rehearsalMarks: string[];
}

export interface ParsedPart {
  id: string;
  name: string;
  abbreviation: string;
  instrumentNames: string[];
  staffCount: number;
  clefs: ClefInfo[];
  /** Notes in written order (measure, then position). */
  notes: RawNote[];
  rests: RawRest[];
  /** Highest number of distinct voices found on any one staff. */
  maxVoicesPerStaff: number;
  graceNoteCount: number;
  hasTranspose: boolean;
}

export interface ParsedScore {
  title: string | null;
  subtitle: string | null;
  composer: string | null;
  lyricist: string | null;
  arranger: string | null;
  parts: ParsedPart[];
  measures: MeasureInfo[];
  tempoMarks: TempoMark[];
  /** Problems found while reading that do not stop parsing. */
  readWarnings: SummaryWarning[];
}

export type WarningCode =
  | 'noTempo'
  | 'noVoiceParts'
  | 'missingVoicePart'
  | 'duplicateVoicePart'
  | 'unmatchedVoice'
  | 'matchedByOrder'
  | 'noLyrics'
  | 'multipleVoicesOnStaff'
  | 'multiStaffVoice'
  | 'octaveClefPitchHigh'
  | 'measureCountMismatch'
  | 'jumpsPresent'
  | 'unrollLimit'
  | 'graceNotesIgnored'
  | 'moreThanTwoLines';

export interface SummaryWarning {
  code: WarningCode;
  partId?: string;
  voicePart?: VoicePart;
  measureNumber?: string;
}

export interface LyricsSummary {
  /** Number of syllables in all verses. */
  syllableCount: number;
  /** Verse numbers that appear, e.g. [1, 2]. */
  verses: number[];
  /** Share of the part's notes that carry at least one syllable, 0 to 1. */
  coverage: number;
  /** The first words of the lowest verse, for the upload screen. */
  preview: string;
}

export interface PartSummary {
  id: string;
  name: string;
  abbreviation: string;
  role: PartRole;
  voicePart: VoicePart | null;
  matchedBy: MatchedBy;
  staffCount: number;
  clefs: ClefInfo[];
  /** True if a staff of this part uses a treble clef that sounds an octave lower. */
  treble8vb: boolean;
  noteCount: number;
  range: { lowestMidi: number; highestMidi: number } | null;
  lyrics: LyricsSummary;
}

/** A voice part that divides into two lines somewhere in the song. */
export interface DividedPart {
  voicePart: VoicePart;
  /** "staff": two voices or chords on the part's staff; "staves": two staves for the part (Soprano 1 and 2). */
  source: 'staff' | 'staves';
  /** Written measure numbers where the lines sing different notes. */
  measureNumbers: string[];
}

export interface TempoSummary {
  measureNumber: string;
  quarterBpm: number;
  beatUnit: string | null;
  dotted: boolean;
  perMinute: number | null;
}

export interface RepeatSummary {
  startMeasureNumber: string;
  endMeasureNumber: string;
  times: number;
  endings: { numbers: number[]; firstMeasureNumber: string; lastMeasureNumber: string }[];
}

export interface JumpSummary {
  type: 'segno' | 'coda' | 'toCoda' | 'daCapo' | 'dalSegno' | 'fine';
  measureNumber: string;
  /** A D.S. or D.C. after which repeats are played ("con rep."). Absent in summaries made before 0.6.7. */
  withRepeats?: boolean;
}

/**
 * What the upload screen shows and what the backend stores as `songs.summary` (jsonb).
 * Bump SUMMARY_VERSION when the shape changes incompatibly.
 */
export interface ScoreSummary {
  summaryVersion: number;
  title: string | null;
  subtitle: string | null;
  composer: string | null;
  lyricist: string | null;
  arranger: string | null;
  parts: PartSummary[];
  /** Measures in the written score. */
  measureCount: number;
  firstMeasureNumber: string;
  lastMeasureNumber: string;
  /** The `number` of each written measure, by index (to show playbackOrder as bar numbers). */
  measureNumbers: string[];
  hasPickup: boolean;
  /** Measures played once repeats and voltas are unrolled. */
  playbackMeasureCount: number;
  /** Indexes (0-based) of the written measures in playback order. */
  playbackOrder: number[];
  durationBeats: number;
  /** Duration at the marked tempo. */
  durationSeconds: number;
  timeSignatures: { measureNumber: string; beats: number; beatType: number }[];
  /** The opening tempo, or null when the score has no tempo mark. */
  tempo: TempoSummary | null;
  tempoChanges: TempoSummary[];
  repeats: RepeatSummary[];
  jumps: JumpSummary[];
  rehearsalMarks: { label: string; measureNumber: string }[];
  /** True if the tenor part uses a treble clef that sounds an octave lower. */
  tenorTreble8: boolean;
  /** Voice parts that divide into two lines (missing in summaries stored before version 0.5.0). */
  dividedParts?: DividedPart[];
  warnings: SummaryWarning[];
}

export interface TimelineMeasure {
  /** Position in playback order. */
  timelineIndex: number;
  /** Index of the written measure. */
  measureIndex: number;
  measureNumber: string;
  /** 1 on the first pass through a repeat, 2 on the second, and so on. */
  pass: number;
  startBeat: number;
  durationBeats: number;
  startSec: number;
  durationSec: number;
}

export interface TimelineNote {
  partId: string;
  /** The voice part, or null for accompaniment. */
  voicePart: VoicePart | null;
  staff: number;
  voice: string;
  /** Sounding MIDI pitch: octave clefs are already at sounding pitch in MusicXML; transposition is applied. */
  midi: number;
  /** The written spelling, for showing note names. */
  spelling: Spelling;
  startBeat: number;
  durationBeats: number;
  startSec: number;
  durationSec: number;
  /** Position in the written score, for cursor mapping. */
  measureIndex: number;
  measureNumber: string;
  /** Offset from the start of the written measure, in quarter notes. */
  beatOffset: number;
  timelineMeasureIndex: number;
  /** The syllable for this pass (verse N on the N-th pass when it exists), or null. */
  lyric: Lyric | null;
  /** The line of a divided part that sings it (0 for every line). */
  line: NoteLine;
}

export interface TempoPoint {
  beat: number;
  sec: number;
  quarterBpm: number;
}

export interface Timeline {
  measures: TimelineMeasure[];
  notes: TimelineNote[];
  tempoMap: TempoPoint[];
  totalBeats: number;
  totalSec: number;
  tempoFactor: number;
}
